import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaTrash, FaPlus, FaMinus, FaCalendarAlt, FaUtensils } from 'react-icons/fa';
import { useSubscriptionCart } from '../../context/SubscriptionCartContext';
import CartLineAddOns from '../../components/CartLineAddOns';
import {
  formatCartLineHostQtyLabel,
  formatCartLineTitle,
  getCartLineHostUnitPrice,
  normalizeCartLineAddOns,
} from '../OurMenu/cartAddOnUtils';
import {
  SUBSCRIPTION_CART_KIND,
  SUBSCRIPTION_CART_SLOT_OPTIONS,
  canChangeSubscriptionCartSlot,
  cartItemDietLabel,
  cartItemKindLabel,
  getSubscriptionCartLineTotal,
  resolveCartItemKind,
  slotLabelForId,
  subscriptionCartLineKey,
} from '../../utils/subscriptionCartItems';
import './Cart.css';
import './SubscriptionCart.css';

function PlanCartItemRow({
  item,
  onRemove,
  onQty,
  onSlotChange,
}) {
  const kind = resolveCartItemKind(item);
  const canChangeSlot = canChangeSubscriptionCartSlot(item);
  const kindLabel = cartItemKindLabel(item);
  const dietLabel = cartItemDietLabel(item);
  const slotSummary = kind === SUBSCRIPTION_CART_KIND.fullDay
    ? (item.slotIds || item.slotProducts?.map((row) => row.slotId) || [])
      .map((slotId) => slotLabelForId(slotId))
      .join(' · ')
    : null;

  return (
    <div className="cart-item">
      <div className="cart-item__main">
        <div className="cart-item__img">
          {item.image ? (
            <img src={item.image} alt={item.name} />
          ) : (
            <span className="sub-cart-img-fallback" aria-hidden>
              {kind === SUBSCRIPTION_CART_KIND.fullDay ? <FaCalendarAlt /> : <FaUtensils />}
            </span>
          )}
        </div>
        <div className="cart-item__body">
          <div className="cart-item__top">
            <div className="cart-item__heading">
              <span className="cart-item__category">
                {kindLabel} · {dietLabel}
                {item.category && kind === SUBSCRIPTION_CART_KIND.alacarte
                  ? ` · ${item.category}`
                  : ''}
              </span>
              <h3 className="cart-item__name">{formatCartLineTitle(item)}</h3>
              {kind === SUBSCRIPTION_CART_KIND.alacarte &&
              normalizeCartLineAddOns(item.addOns).length === 0 ? (
                <span className="cart-item__host-meta">
                  {formatCartLineHostQtyLabel(
                    item.quantity,
                    getCartLineHostUnitPrice(item),
                  )}
                </span>
              ) : null}
              {kind !== SUBSCRIPTION_CART_KIND.alacarte ? (
                <span className="cart-item__unit-price">
                  ₹{Number(item.price || 0).toFixed(2)} each
                </span>
              ) : null}
              {slotSummary ? (
                <p className="sub-cart-slot-summary">{slotSummary}</p>
              ) : null}
              {canChangeSlot ? (
                <label className="sub-cart-slot-field">
                  <span>Meal slot</span>
                  <select
                    value={item.slotId}
                    onChange={(e) => onSlotChange(item, e.target.value)}
                    aria-label={`Meal slot for ${item.name}`}
                  >
                    {SUBSCRIPTION_CART_SLOT_OPTIONS.map((slot) => (
                      <option key={slot.id} value={slot.id}>
                        {slot.label}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
            </div>
            <div className="cart-item__controls">
              <div className="qty-stepper">
                <button
                  type="button"
                  className="qty-btn"
                  onClick={() => onQty(item, item.quantity - 1)}
                  aria-label="Decrease quantity"
                >
                  <FaMinus />
                </button>
                <span className="qty-value">{item.quantity}</span>
                <button
                  type="button"
                  className="qty-btn"
                  onClick={() => onQty(item, item.quantity + 1)}
                  aria-label="Increase quantity"
                >
                  <FaPlus />
                </button>
              </div>
              <span className="cart-item__line-total">
                ₹{getSubscriptionCartLineTotal(item).toFixed(2)}
              </span>
              <button
                type="button"
                className="cart-item__remove"
                onClick={() => onRemove(item)}
                aria-label={`Remove ${item.name}`}
              >
                <FaTrash />
              </button>
            </div>
          </div>
          {kind === SUBSCRIPTION_CART_KIND.alacarte &&
          normalizeCartLineAddOns(item.addOns).length > 0 ? (
            <CartLineAddOns
              variant="formula"
              hostName={formatCartLineTitle(item)}
              hostQty={item.quantity}
              hostUnitPrice={getCartLineHostUnitPrice(item)}
              addOns={item.addOns}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

const SubscriptionCart = () => {
  const {
    subscriptionCartItems,
    removeFromSubscriptionCart,
    updateSubscriptionCartQty,
    updateSubscriptionCartSlot,
    clearSubscriptionCart,
    subscriptionCartCount,
    subscriptionCartTotal,
  } = useSubscriptionCart();
  const navigate = useNavigate();

  const fullDayItems = subscriptionCartItems.filter(
    (item) => resolveCartItemKind(item) === SUBSCRIPTION_CART_KIND.fullDay,
  );
  const groupedSlots = SUBSCRIPTION_CART_SLOT_OPTIONS.map((slot) => ({
    ...slot,
    items: subscriptionCartItems.filter((item) => (
      resolveCartItemKind(item) !== SUBSCRIPTION_CART_KIND.fullDay && item.slotId === slot.id
    )),
  })).filter((group) => group.items.length > 0);

  const handleRemove = (item) => {
    const lineKey = subscriptionCartLineKey(item);
    if (resolveCartItemKind(item) === SUBSCRIPTION_CART_KIND.fullDay) {
      removeFromSubscriptionCart(item.fullDayGroupId || item.id, null, { lineKey });
      return;
    }
    removeFromSubscriptionCart(item.id, item.slotId, { lineKey });
  };

  const handleQty = (item, qty) => {
    const lineKey = subscriptionCartLineKey(item);
    if (resolveCartItemKind(item) === SUBSCRIPTION_CART_KIND.fullDay) {
      updateSubscriptionCartQty(item.fullDayGroupId || item.id, null, qty, { lineKey });
      return;
    }
    updateSubscriptionCartQty(item.id, item.slotId, qty, { lineKey });
  };

  const handleSlotChange = (item, toSlotId) => {
    updateSubscriptionCartSlot(item.id, item.slotId, toSlotId, {
      lineKey: subscriptionCartLineKey(item),
    });
  };

  if (subscriptionCartItems.length === 0) {
    return (
      <div className="cart-page">
        <div className="page-header">
          <div className="page-header-content">
            <div className="container">
              <h1>SUBSCRIPTION CART</h1>
              <div className="breadcrumb">
                <Link to="/">Home</Link>
                <span>/</span>
                <span>Subscription cart</span>
              </div>
            </div>
          </div>
        </div>
        <section className="cart-empty">
          <div className="container">
            <FaCalendarAlt className="cart-empty__icon" />
            <h2>Your subscription cart is empty</h2>
            <p>
              Add specialty packages, Full Day plans, or Our menu dishes. They stay here until you
              build and submit the subscription.
            </p>
            <div className="sub-cart-empty-actions">
              <Link to="/add-subscription" className="btn-primary">
                BUILD A SUBSCRIPTION
              </Link>
              <Link to="/our-menu" className="sub-cart-secondary-link">
                Browse Our menu
              </Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="cart-page">
      <div className="page-header">
        <div className="page-header-content">
          <div className="container">
            <h1>SUBSCRIPTION CART</h1>
            <div className="breadcrumb">
              <Link to="/">Home</Link>
              <span>/</span>
              <span>Subscription cart</span>
            </div>
          </div>
        </div>
      </div>

      <section className="cart-section">
        <div className="container">
          <div className="cart-layout">
            <div className="cart-items">
              <div className="cart-items__header">
                <h2>
                  Plan items{' '}
                  <span>
                    ({subscriptionCartCount} {subscriptionCartCount === 1 ? 'item' : 'items'})
                  </span>
                </h2>
                <button type="button" className="cart-clear-btn" onClick={clearSubscriptionCart}>
                  Clear All
                </button>
              </div>

              <p className="sub-cart-hint">
                Specialty packages, Full Day, and Our menu (Regular) items are saved in this cart
                and restored when you continue building your subscription.
              </p>

              {fullDayItems.length > 0 ? (
                <div className="sub-cart-slot-group">
                  <h3 className="sub-cart-slot-group__title">Full Day</h3>
                  {fullDayItems.map((item) => (
                    <PlanCartItemRow
                      key={subscriptionCartLineKey(item)}
                      item={item}
                      onRemove={handleRemove}
                      onQty={handleQty}
                    />
                  ))}
                </div>
              ) : null}

              {groupedSlots.map((group) => (
                <div key={group.id} className="sub-cart-slot-group">
                  <h3 className="sub-cart-slot-group__title">{group.label}</h3>
                  {group.items.map((item) => (
                    <PlanCartItemRow
                      key={subscriptionCartLineKey(item)}
                      item={item}
                      onRemove={handleRemove}
                      onQty={handleQty}
                      onSlotChange={handleSlotChange}
                    />
                  ))}
                </div>
              ))}

              <div className="cart-continue">
                <Link to="/add-subscription" className="cart-continue__link">
                  ← Add specialties or Full Day
                </Link>
                <Link to="/our-menu" className="cart-continue__link">
                  Browse Our menu
                </Link>
              </div>
            </div>

            <div className="cart-summary">
              <h2 className="cart-summary__title">Plan items</h2>
              <div className="cart-breakdown">
                <div className="cart-breakdown__row">
                  <span>Subtotal (per delivery day)</span>
                  <span>₹{subscriptionCartTotal.toFixed(2)}</span>
                </div>
                <p className="sub-cart-summary-note">
                  Final subscription total depends on dates, delivery days, and charges in the
                  builder.
                </p>
              </div>
              <button
                type="button"
                className="cart-checkout-btn"
                onClick={() => navigate('/add-subscription')}
              >
                Continue subscription
              </button>
              <Link to="/cart" className="sub-cart-regular-link">
                View regular cart
              </Link>
            </div>
          </div>
        </div>
      </section>

      <div className="cart-mobile-bar">
        <div className="cart-mobile-bar__total">
          <span>Subtotal / day</span>
          <strong>₹{subscriptionCartTotal.toFixed(2)}</strong>
        </div>
        <button
          type="button"
          className="cart-mobile-bar__pay"
          onClick={() => navigate('/add-subscription')}
        >
          Continue
        </button>
      </div>
    </div>
  );
};

export default SubscriptionCart;
